import { type ColumnDef } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import { UserAvatar } from '@/shared/components/custom/avatars/UserAvatar';
import { getUserColor } from '@/core/lib/colors';
import type { Member } from '../types';
import { ClientMemberRowActions } from '../components/ClientMemberRowActions';
import { useInstantFormat } from '@/shared/hooks/useInstantFormat';

interface ClientMemberColumnsOptions {
  onDelete: (memberId: string) => void;
  isDeleteLoading?: (memberId: string) => boolean;
  t: TFunction;
}

function CreatedAtCell({ member }: { member: Member }) {
  const formatInstant = useInstantFormat();
  return <span className="text-sm text-muted-foreground">{formatInstant(member.createdAt)}</span>;
}

function ExpiresAtCell({ member, t }: { member: Member; t: TFunction }) {
  const formatInstant = useInstantFormat();
  if (!member.expiresAt) {
    return <span className="text-sm text-muted-foreground">{t('noExpiration')}</span>;
  }
  return <span className="text-sm">{formatInstant(member.expiresAt)}</span>;
}

export function getClientMemberColumns(
  options: ClientMemberColumnsOptions,
): ColumnDef<Member, unknown>[] {
  const { onDelete, isDeleteLoading, t } = options;

  return [
    {
      accessorKey: 'displayName',
      header: t('table.member'),
      meta: { cellClassName: 'flex-[2] min-w-0' },
      cell: ({ row }) => {
        const member = row.original;
        return (
          <div className="flex items-center gap-3">
            <UserAvatar
              name={member.displayName}
              color={getUserColor(member.userId || member.id)}
              src={member.avatarUrl ?? undefined}
              size="sm"
            />
            <div>
              <div className="font-medium text-foreground">{member.displayName}</div>
              <div className="text-xs text-muted-foreground">{member.email}</div>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'expiresAt',
      header: t('table.expiration'),
      meta: { cellClassName: 'flex-1' },
      cell: ({ row }) => <ExpiresAtCell member={row.original} t={t} />,
    },
    {
      accessorKey: 'createdAt',
      header: t('table.joinedAt'),
      meta: { cellClassName: 'flex-1' },
      cell: ({ row }) => <CreatedAtCell member={row.original} />,
    },
    {
      id: 'actions',
      header: undefined,
      meta: { cellClassName: 'w-12 flex justify-end' },
      cell: ({ row }) => {
        const member = row.original;
        return (
          <ClientMemberRowActions
            member={member}
            onDelete={onDelete}
            isLoading={isDeleteLoading}
            t={t}
          />
        );
      },
    },
  ];
}
